import { hasHtmlContent } from 'utils/helpers/functions/stripHtml';
import {
    hasTemplateTableMarker,
    normalizeTemplateMarkers,
    TEMPLATE_MARKER_REGEX,
} from './templateMarkers';

export interface PreviewRow {
    rowId: string;
    name: string;
    count: number;
    price: number;
    total: number;
    vat?: number;
}

export type PreviewBlock =
    | { kind: 'html'; id: string; html: string }
    | { kind: 'section-title'; id: string; text: string }
    | { kind: 'empty'; id: string; text: string }
    | { kind: 'page-break'; id: string }
    | { kind: 'table-header'; id: string }
    | { kind: 'table-row'; id: string; row: PreviewRow }
    | { kind: 'table-footer'; id: string; totalLabel: string; totalValue: string; vatValue?: string };

export type ContentPage = {
    id: string;
    blocks: PreviewBlock[];
};

export type MeasureLabels = {
    tableName: string;
    tableCount: string;
    tablePrice: string;
    tableTotal: string;
    tableVat?: string;
    withVat?: boolean;
};

export type MarkedSectionTableParams = {
    specificationRows: PreviewRow[];
    productsRows: PreviewRow[];
    additionalsRows: PreviewRow[];
    specificationTotalLabel: string;
    specificationTotalValue: string;
    specificationVatValue?: string;
    productsTotalLabel: string;
    productsTotalValue: string;
    productsVatValue?: string;
    additionalsTotalLabel: string;
    additionalsTotalValue: string;
    additionalsVatValue?: string;
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

const isTablePart = (block: PreviewBlock) =>
    block.kind === 'table-header' || block.kind === 'table-row' || block.kind === 'table-footer';

const outerHeight = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return Math.ceil(
        rect.height + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0),
    );
};

export const getPageMetrics = () => {
    const page = document.createElement('div');
    page.className = 'proposal-preview-a4-page';
    page.style.cssText =
        'position:absolute;left:-10000px;top:0;visibility:hidden;pointer-events:none;height:297mm;';
    document.body.appendChild(page);
    const pageRect = page.getBoundingClientRect();
    const styles = window.getComputedStyle(page);
    const padTop = parseFloat(styles.paddingTop) || 0;
    const padBottom = parseFloat(styles.paddingBottom) || 0;
    const padLeft = parseFloat(styles.paddingLeft) || 0;
    const padRight = parseFloat(styles.paddingRight) || 0;
    document.body.removeChild(page);

    // Reserve space for page number footer inside the page
    const pageNumberReserve = 28;

    return {
        pageHeightPx: Math.floor(pageRect.height),
        contentHeightPx: Math.floor(pageRect.height - padTop - padBottom - pageNumberReserve),
        contentWidthPx: Math.floor(pageRect.width - padLeft - padRight),
    };
};

export const splitHtmlToBlocks = (html: string, prefix: string): PreviewBlock[] => {
    if (!hasHtmlContent(html)) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const children = Array.from(doc.body.children);
    if (!children.length) {
        return [{ kind: 'html', id: `${prefix}-0`, html }];
    }
    return children
        .filter((el) => hasHtmlContent(el.outerHTML))
        .map((el, index) => ({
            kind: 'html' as const,
            id: `${prefix}-${index}`,
            html: el.outerHTML,
        }));
};

const buildTableBlocks = (params: {
    rows: PreviewRow[];
    totalLabel: string;
    totalValue: string;
    vatValue?: string;
    idPrefix?: string;
}): PreviewBlock[] => {
    if (!params.rows.length) return [];
    const idPrefix = params.idPrefix || 'table';
    return [
        { kind: 'table-header', id: `${idPrefix}-header` },
        ...params.rows.map((row, index) => ({
            kind: 'table-row' as const,
            id: `${idPrefix}-row-${row.rowId || index}`,
            row,
        })),
        {
            kind: 'table-footer',
            id: `${idPrefix}-footer`,
            totalLabel: params.totalLabel,
            totalValue: params.totalValue,
            vatValue: params.vatValue,
        },
    ];
};

/** Expands `{specification|products|additionals}` markers into table preview blocks. */
export const buildMarkedSectionBlocks = (
    html: string,
    prefix: string,
    tableParams: MarkedSectionTableParams,
    fallbackAppendTable = false,
): PreviewBlock[] => {
    const createTableBlocksByMarker = (marker: string, idPrefix: string): PreviewBlock[] => {
        if (marker === 'products') {
            return buildTableBlocks({
                rows: tableParams.productsRows,
                totalLabel: tableParams.productsTotalLabel,
                totalValue: tableParams.productsTotalValue,
                vatValue: tableParams.productsVatValue,
                idPrefix,
            });
        }
        if (marker === 'additionals') {
            return buildTableBlocks({
                rows: tableParams.additionalsRows,
                totalLabel: tableParams.additionalsTotalLabel,
                totalValue: tableParams.additionalsTotalValue,
                vatValue: tableParams.additionalsVatValue,
                idPrefix,
            });
        }
        return buildTableBlocks({
            rows: tableParams.specificationRows,
            totalLabel: tableParams.specificationTotalLabel,
            totalValue: tableParams.specificationTotalValue,
            vatValue: tableParams.specificationVatValue,
            idPrefix,
        });
    };

    const sourceHtml = normalizeTemplateMarkers(html);
    const blocks: PreviewBlock[] = [];
    const markerMatches = sourceHtml.match(TEMPLATE_MARKER_REGEX);
    const hasMarker = Boolean(markerMatches?.length);

    if (hasMarker) {
        let segmentStart = 0;
        let segmentIndex = 0;
        let markerIndex = 0;
        const regex = new RegExp(TEMPLATE_MARKER_REGEX);
        let match: RegExpExecArray | null = null;

        while ((match = regex.exec(sourceHtml))) {
            const segment = sourceHtml.slice(segmentStart, match.index);
            blocks.push(...splitHtmlToBlocks(segment, `${prefix}-${segmentIndex}`));
            segmentIndex += 1;

            const marker = String(match[1] || '').toLowerCase();
            blocks.push(...createTableBlocksByMarker(marker, `${prefix}-table-${marker}-${markerIndex}`));
            markerIndex += 1;
            segmentStart = match.index + match[0].length;
        }

        const tailSegment = sourceHtml.slice(segmentStart);
        blocks.push(...splitHtmlToBlocks(tailSegment, `${prefix}-${segmentIndex}`));
        return blocks;
    }

    blocks.push(...splitHtmlToBlocks(sourceHtml, prefix));
    if (fallbackAppendTable) {
        blocks.push(...createTableBlocksByMarker('specification', `${prefix}-table-fallback`));
    }
    return blocks;
};

export const buildContentBlocks = (
    params: MarkedSectionTableParams & {
        introHtml: string;
        outroHtml: string;
        /** When title page already expands a table marker — do not auto-append to intro. */
        skipFallbackTable?: boolean;
    },
): PreviewBlock[] => {
    const introHtml = String(params.introHtml || '');
    const outroHtml = String(params.outroHtml || '');
    const introHasMarker = hasTemplateTableMarker(introHtml);
    const outroHasMarker = hasTemplateTableMarker(outroHtml);
    const introHasText = hasHtmlContent(introHtml);
    const outroHasText = hasHtmlContent(outroHtml);
    const fallbackAppendTable =
        !params.skipFallbackTable && !introHasMarker && !outroHasMarker;

    const introBlocks = introHasText
        ? buildMarkedSectionBlocks(introHtml, 'intro', params, fallbackAppendTable)
        : [];
    const outroBlocks = outroHasText
        ? buildMarkedSectionBlocks(outroHtml, 'outro', params, false)
        : [];

    if (!outroBlocks.length) return introBlocks;
    if (!introBlocks.length) return outroBlocks;
    return [...introBlocks, { kind: 'page-break', id: 'outro-page-break' }, ...outroBlocks];
};

const gapBetween = (prev: PreviewBlock | null, next: PreviewBlock, sectionGapPx: number) => {
    if (!prev) return 0;
    if (prev.kind === 'page-break' || next.kind === 'page-break') return 0;
    if (isTablePart(prev) && isTablePart(next)) return 0;
    if (prev.kind === 'html' && next.kind === 'html') return 0;
    if (prev.kind === 'section-title' && (next.kind === 'table-header' || next.kind === 'empty')) {
        return 0;
    }
    return sectionGapPx;
};

export const packBlocksIntoPages = (
    blocks: PreviewBlock[],
    heights: Record<string, number>,
    pageHeight: number,
    sectionGapPx = 16,
): ContentPage[] => {
    const pages: ContentPage[] = [];
    let current: PreviewBlock[] = [];
    let used = 0;
    let pageIndex = 0;
    const available = Math.max(1, pageHeight - 6);

    const headerSource = blocks.find((b) => b.kind === 'table-header') as
        | Extract<PreviewBlock, { kind: 'table-header' }>
        | undefined;
    const headerHeight = headerSource ? heights[headerSource.id] || 0 : 0;

    const pushPage = () => {
        if (!current.length) return;
        pages.push({
            id: `content-page-${pageIndex++}`,
            blocks: current,
        });
        current = [];
        used = 0;
    };

    for (const block of blocks) {
        if (block.kind === 'page-break') {
            pushPage();
            continue;
        }
        const height = heights[block.id] || 0;
        const prev = current[current.length - 1] || null;
        const gap = gapBetween(prev, block, sectionGapPx);

        if (current.length && used + gap + height > available) {
            pushPage();

            if (block.kind === 'table-row' && headerSource) {
                current.push({
                    ...headerSource,
                    id: `${headerSource.id}-cont-${pageIndex}`,
                });
                used = headerHeight;
            }
        }

        const prev2 = current[current.length - 1] || null;
        used += gapBetween(prev2, block, sectionGapPx) + height;
        current.push(block);
    }

    pushPage();
    return pages;
};

export const measurePreviewBlocks = (
    blocks: PreviewBlock[],
    labels: MeasureLabels,
    contentWidthPx: number,
): Record<string, number> => {
    const host = document.createElement('div');
    host.className = 'proposal-preview-a4-page proposal-preview-measure-host';
    host.style.cssText = [
        'position:absolute',
        'left:-10000px',
        'top:0',
        'visibility:hidden',
        'pointer-events:none',
        `width:${contentWidthPx}px`,
        'height:auto',
        'min-height:0',
        'max-height:none',
        'padding:0',
        'border:none',
        'box-shadow:none',
        'margin:0',
        'overflow:visible',
    ].join(';');

    const nodes: Array<{ id: string; el: HTMLElement }> = [];

    const add = (id: string, el: HTMLElement) => {
        el.dataset.measureId = id;
        host.appendChild(el);
        nodes.push({ id, el });
    };

    blocks.forEach((block) => {
        if (block.kind === 'page-break') return;
        if (block.kind === 'html') {
            const el = document.createElement('div');
            el.className = 'proposal-preview-rich-text';
            el.innerHTML = block.html;
            add(block.id, el);
            return;
        }
        if (block.kind === 'section-title') {
            const el = document.createElement('h2');
            el.className = 'proposal-preview-section-title';
            el.textContent = block.text;
            add(block.id, el);
            return;
        }
        if (block.kind === 'empty') {
            const el = document.createElement('p');
            el.className = 'proposal-preview-empty';
            el.textContent = block.text;
            add(block.id, el);
            return;
        }
        if (block.kind === 'table-header') {
            const table = document.createElement('table');
            table.className = 'proposal-preview-table proposal-preview-table--measure-header';
            const vatHeader = labels.withVat
                ? `<th class="proposal-preview-col-num">${escapeHtml(labels.tableVat || '')}</th>`
                : '';
            table.innerHTML = `<thead><tr>
                <th>${escapeHtml(labels.tableName)}</th>
                <th class="proposal-preview-col-num">${escapeHtml(labels.tableCount)}</th>
                <th class="proposal-preview-col-num">${escapeHtml(labels.tablePrice)}</th>
                <th class="proposal-preview-col-num">${escapeHtml(labels.tableTotal)}</th>
                ${vatHeader}
            </tr></thead>`;
            add(block.id, table);
            return;
        }
        if (block.kind === 'table-row') {
            const table = document.createElement('table');
            table.className = 'proposal-preview-table proposal-preview-table--measure-row';
            const vatCell = labels.withVat
                ? '<td class="proposal-preview-col-num">0.00</td>'
                : '';
            table.innerHTML = `<tbody><tr>
                <td>${escapeHtml(block.row.name)}</td>
                <td class="proposal-preview-col-num">0.00</td>
                <td class="proposal-preview-col-num">0.00</td>
                <td class="proposal-preview-col-num">0.00</td>
                ${vatCell}
            </tr></tbody>`;
            add(block.id, table);
            return;
        }
        if (block.kind === 'table-footer') {
            const table = document.createElement('table');
            table.className = 'proposal-preview-table proposal-preview-table--measure-footer';
            const vatCell = labels.withVat
                ? `<td class="proposal-preview-col-num proposal-preview-total-value">${escapeHtml(block.vatValue || '0.00')}</td>`
                : '';
            table.innerHTML = `<tfoot><tr>
                <td colspan="3" class="proposal-preview-total-label">${escapeHtml(block.totalLabel)}</td>
                <td class="proposal-preview-col-num proposal-preview-total-value">${escapeHtml(block.totalValue)}</td>
                ${vatCell}
            </tr></tfoot>`;
            add(block.id, table);
        }
    });

    document.body.appendChild(host);

    const heights: Record<string, number> = {};
    nodes.forEach(({ id, el }) => {
        heights[id] = Math.max(1, outerHeight(el));
    });

    document.body.removeChild(host);
    return heights;
};
